/**
 * AdminRiskProfiles — Super Admin compliance dashboard listing every
 * user with their Investment Strategy completion status + risk category.
 * Supports filters by tier, category, agency, completion state, and text search.
 * Sub-admins access a scoped-per-agency version at /agency-dashboard.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, ShieldCheck, ShieldAlert, Search } from "lucide-react";
import DashboardGridWrapper from "@/components/dashboard/DashboardGridWrapper";
import PageHeader from "@/components/dashboard/PageHeader";
import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";

const CAT_LABELS = {
    conservative:   "Capital Preservation",
    below_average:  "Income w/ Growth",
    average:        "Balanced",
    above_average:  "Growth w/ Income",
    aggressive:     "Growth (Aggressive)",
};

const CAT_COLOR = {
    conservative:   "bg-blue-500/15 text-blue-500 border-blue-500/40",
    below_average:  "bg-teal-500/15 text-teal-500 border-teal-500/40",
    average:        "bg-primary/15 text-primary border-primary/40",
    above_average:  "bg-amber-500/15 text-amber-500 border-amber-500/40",
    aggressive:     "bg-danger/15 text-danger border-danger/40",
};

export default function AdminRiskProfiles() {
    const [rows, setRows]   = useState(null);
    const [q, setQ]         = useState("");
    const [tier, setTier]   = useState("");
    const [category, setCategory] = useState("");
    const [status, setStatus] = useState(""); // "" | "completed" | "pending"

    async function load() {
        setRows(null);
        const params = new URLSearchParams();
        if (q)        params.set("q", q);
        if (tier)     params.set("tier", tier);
        if (category) params.set("category", category);
        if (status === "completed") params.set("completed", "true");
        if (status === "pending")   params.set("completed", "false");
        try {
            const { data } = await api.get(`/admin/investment-strategies?${params.toString()}`);
            setRows(data.rows || []);
        } catch (e) {
            toast.error("Could not load compliance list");
            setRows([]);
        }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tier, category, status]);

    const totals = useMemo(() => {
        if (!rows) return null;
        const t = { total: rows.length, completed: 0, pending: 0 };
        rows.forEach((r) => { r.completed ? t.completed++ : t.pending++; });
        t.rate = t.total > 0 ? Math.round((t.completed / t.total) * 100) : 0;
        return t;
    }, [rows]);
    const { sorted: sortedRows, sortKey, direction, requestSort } = useSortableData(rows, {
        accessors: {
            user: (r) => r.full_name || r.email || "",
            tier: (r) => r.tier || "",
            status: (r) => r.completed ? "completed" : "pending",
            category: (r) => r.category || "",
            score: (r) => Number(r.score ?? -Infinity),
        },
    });

    async function viewPdf(strategyId) {
        try {
            const r = await api.get(`/admin/investment-strategies/${strategyId}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            window.open(url, "_blank", "noopener");
            setTimeout(() => URL.revokeObjectURL(url), 20000);
        } catch {
            toast.error("Could not open PDF");
        }
    }

    const widgets = [
        {
            id: "rp-summary", title: "Compliance summary", category: "Overview", layout: { x: 0, y: 0, w: 12, h: 3 }, minW: 6, minH: 2, noPadding: true,
            render: () => (
                totals ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3">
                        <Kpi test="rp-kpi-total"     label="Users"        value={totals.total} />
                        <Kpi test="rp-kpi-completed" label="Completed"    value={totals.completed} color="text-emerald-500" />
                        <Kpi test="rp-kpi-pending"   label="Pending"      value={totals.pending}   color="text-danger" />
                        <Kpi test="rp-kpi-rate"      label="Completion %" value={`${totals.rate}%`} color="text-primary" />
                    </div>
                ) : null
            ),
        },
        {
            id: "rp-table", title: "Risk profiles", category: "Compliance", layout: { x: 0, y: 3, w: 12, h: 14 }, minW: 6, minH: 6,
            render: () => (
                <div className="rounded-xl border-2 border-border bg-card overflow-hidden" data-testid="rp-table">
                    <table className="w-full text-sm">
                        <thead className="bg-card border-b border-border">
                            <tr>
                                <SortableTh label="User" sortKey="user" currentKey={sortKey} direction={direction} onSort={requestSort} className="text-xs uppercase tracking-widest" />
                                <SortableTh label="Tier" sortKey="tier" currentKey={sortKey} direction={direction} onSort={requestSort} className="text-xs uppercase tracking-widest" />
                                <SortableTh label="Status" sortKey="status" currentKey={sortKey} direction={direction} onSort={requestSort} className="text-xs uppercase tracking-widest" />
                                <SortableTh label="Category" sortKey="category" currentKey={sortKey} direction={direction} onSort={requestSort} className="text-xs uppercase tracking-widest" />
                                <SortableTh label="Score" sortKey="score" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="text-xs uppercase tracking-widest" />
                                <th className="text-right px-4 py-3 text-xs uppercase tracking-widest text-muted-foreground">PDF</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows === null ? (
                                <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>
                            ) : sortedRows.length === 0 ? (
                                <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No users match these filters.</td></tr>
                            ) : sortedRows.map((r) => (
                                <tr key={r.user_id} className="border-t border-border hover:bg-card/60" data-testid={`rp-row-${r.user_id}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-semibold">{r.full_name || "—"}</div>
                                        <div className="text-xs text-muted-foreground break-all">{r.email}</div>
                                    </td>
                                    <td className="px-4 py-3 text-xs uppercase">{r.tier || "—"}</td>
                                    <td className="px-4 py-3">
                                        {r.completed ? (
                                            <span className="inline-flex items-center gap-1 text-xs text-emerald-500 font-semibold">
                                                <ShieldCheck className="w-3.5 h-3.5" /> Completed
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-xs text-danger font-semibold">
                                                <ShieldAlert className="w-3.5 h-3.5" /> Pending
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {r.category ? (
                                            <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide border rounded ${CAT_COLOR[r.category] || ""}`}>
                                                {CAT_LABELS[r.category] || r.category}
                                            </span>
                                        ) : <span className="text-xs text-muted-foreground">—</span>}
                                    </td>
                                    <td className="px-4 py-3 text-right mono">{r.score ?? "—"}</td>
                                    <td className="px-4 py-3 text-right">
                                        {r.completed && r.strategy_id ? (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => viewPdf(r.strategy_id)}
                                                data-testid={`rp-pdf-btn-${r.user_id}`}
                                            >
                                                <FileText className="w-3.5 h-3.5 mr-1" /> View
                                            </Button>
                                        ) : <span className="text-xs text-muted-foreground">—</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ),
        },
    ];

    return (
        <div className="space-y-6" data-testid="admin-risk-profiles">
                <PageHeader
                    icon={ShieldAlert}
                    eyebrow="Admin Tools"
                    title="Risk Profiles"
                    subtitle="Every trader must complete the 10-question Investment Strategy questionnaire before their first trade. Filter, search, and download signed PDFs below."
                    testId="admin-risk-profiles-header"
                />

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-2" data-testid="rp-filters">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Search name or email"
                            className="pl-8 w-64"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && load()}
                            data-testid="rp-search-input"
                        />
                    </div>
                    <Select label="Tier"      value={tier}     onChange={setTier}      options={["", "individual", "advanced", "institution_moe", "institution_cma"]} test="rp-filter-tier" />
                    <Select label="Category"  value={category} onChange={setCategory}  options={["", "conservative", "below_average", "average", "above_average", "aggressive"]} test="rp-filter-cat" />
                    <Select label="Status"    value={status}   onChange={setStatus}    options={["", "completed", "pending"]} test="rp-filter-status" />
                    <Button variant="outline" onClick={load} data-testid="rp-refresh-btn">Refresh</Button>
                </div>

                <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-risk-profiles" widgets={widgets} />
        </div>
    );
}

function Kpi({ label, value, color = "text-foreground", test }) {
    return (
        <div className="rounded-xl border-2 border-border bg-card p-4" data-testid={test}>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className={`text-3xl font-extrabold tracking-tight mt-1 mono whitespace-nowrap ${color}`}>{value}</div>
        </div>
    );
}

function Select({ label, value, onChange, options, test }) {
    return (
        <label className="inline-flex items-center gap-1.5 text-xs" data-testid={test}>
            <span className="text-muted-foreground">{label}:</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="bg-card border border-border rounded px-2 py-1.5 text-xs"
            >
                {options.map((o) => (
                    <option key={o} value={o}>{o === "" ? "any" : o.replaceAll("_", " ")}</option>
                ))}
            </select>
        </label>
    );
}
